import { useMemo, useRef } from 'react';
import { ICollectionTag } from '@monofront/types';
import {
  Carousel,
  CarouselSlide,
} from '@monofront/design-system/components/Carousel';
import HyperLink, {
  THyperLinkVariant,
} from '@monofront/design-system/components/HyperLink';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { space } from '@monofront/theme';
import {
  COLLECTIONS_TAGS,
  FRESH_USER,
  ULTRA_FRESH_USER,
} from '@monofront/business/constants';
import { useUserTags } from '@monofront/business/hooks';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';

interface ICarouselTagsProps {
  tagList: ICollectionTag[];
  hyperlinkVariant?: THyperLinkVariant;
  onClick?: (collection: ICollectionTag) => void;
}

const CarouselTags: React.FC<ICarouselTagsProps> = ({
  tagList,
  hyperlinkVariant = 'button-primary-transparent',
  onClick,
}) => {
  const navigationNextRef = useRef<HTMLButtonElement>(null);
  const navigationPrevRef = useRef<HTMLButtonElement>(null);
  const localTags = useUserTags(useCacheFirstAuthentication);
  const tGlobal = useTranslations('Global');

  const displayedCollections = useMemo(() => {
    return (
      tagList?.filter(
        (collection) =>
          (!collection._tags?.includes(COLLECTIONS_TAGS.FRESH) ||
            localTags.includes(FRESH_USER)) &&
          (!collection._tags?.includes(COLLECTIONS_TAGS.ULTRA_FRESH) ||
            localTags.includes(ULTRA_FRESH_USER)),
      ) || []
    );
  }, [tagList, localTags]);

  return (
    <>
      <section className="container carousel-tags lf-carousel">
        <Carousel
          slidesPerView={'auto'}
          slidesPerGroupAuto={true}
          spaceBetween={15}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          // Swiper breakpoints are `min-width` and not `max-width`!
          breakpoints={{
            800: {
              slidesOffsetBefore: space[3],
            },
          }}
          slidesOffsetBefore={0}
        >
          {displayedCollections.map((tag) => {
            return (
              <CarouselSlide
                key={tag.objectID || tag.handle}
                onClick={() => onClick?.(tag)}
              >
                <Link
                  href={`/collections/${tag.handle}`}
                  passHref
                  legacyBehavior
                >
                  <HyperLink
                    variant={hyperlinkVariant}
                    dataTestId={`Collection-CarouselTag-${tag.handle}`}
                  >
                    {tag.title}
                  </HyperLink>
                </Link>
              </CarouselSlide>
            );
          })}
        </Carousel>
        <button
          ref={navigationPrevRef}
          type="button"
          className="swiper-button-prev"
          aria-label={tGlobal('previous')}
          data-testid="CarouselTags-button-swiper-button-prev"
        />
        <button
          ref={navigationNextRef}
          type="button"
          className="swiper-button-next"
          aria-label={tGlobal('next')}
          data-testid="CarouselTags-button-swiper-button-next"
        />
      </section>
      <style jsx>{`
        .carousel-tags {
          // lots of calc to insure the correct sizing and positioning of the tags and carousel buttons
          --tags-line-height: 1.7rem;
          --tag-size: (var(--space-2) * 2) + var(--tags-line-height);
          position: relative;
          margin-top: 1rem;
          width: calc(100% + var(--space-5));
        }

        .carousel-tags :global(.swiper-button-disabled) {
          display: none;
        }
        .carousel-tags :global(.swiper-button-next),
        .carousel-tags :global(.swiper-button-prev) {
          top: 1px;
          margin-top: 0;
          width: calc(var(--tag-size));
          height: calc(var(--tag-size));
        }
        .carousel-tags :global(.swiper-button-next) {
          right: calc((calc(var(--tag-size)) / 2) * -1);
        }
        .carousel-tags :global(.swiper-button-prev) {
          left: -15px;
        }

        .carousel-tags :global(.swiper) {
          padding-right: 3rem;
        }

        .carousel-tags :global(a) {
          line-height: var(--tags-line-height);
        }

        @media (max-width: 800px) {
          .carousel-tags {
            margin-top: 0;
            width: 100%;
          }

          .carousel-tags :global(a) {
            height: 36px;
          }

          .carousel-tags :global(.swiper) {
            padding-right: 0;
          }
        }
      `}</style>
    </>
  );
};

export default CarouselTags;
